import type { Option, Scenario, ShowIf } from "../entities";
import type { LocalizedText } from "../localized";

export const MIGREBOT_SCENARIO_ID = "7d3a4c1e-6b2f-4f0e-9a5c-1c2d3e4f5a01";

const painYes: ShowIf = { questionId: "hadPain", equals: true };
const details: ShowIf = { questionId: "detailsWanted", equals: true };

const option = (id: string, text: LocalizedText): Option => ({ id, text });

/**
 * Headache diary modelled on the Migrebot dialog (wording taken from the bot,
 * see docs/reference/migrebot-dialog.md). Several attacks per day are allowed.
 */
export const migrebotScenario: Scenario = {
  id: MIGREBOT_SCENARIO_ID,
  slug: "migrebot",
  version: 2,
  builtIn: true,
  name: {
    en: "Headache diary", ru: "Дневник головной боли", de: "Kopfschmerz-Tagebuch", fr: "Journal des maux de tête",
    es: "Diario del dolor de cabeza", tr: "Baş ağrısı günlüğü", hi: "सिरदर्द डायरी", sr: "Дневник главобоље",
  },
  description: {
    en: "One question a day: did your head hurt. If yes, a few short follow-ups.",
    ru: "Один вопрос в день: болела ли голова. Если да, несколько коротких уточнений.",
    de: "Eine Frage pro Tag: Hatten Sie Kopfschmerzen? Wenn ja, ein paar kurze Nachfragen.",
    fr: "Une question par jour : avez-vous eu mal à la tête ? Si oui, quelques précisions rapides.",
    es: "Una pregunta al día: ¿te dolió la cabeza? Si es así, unas breves preguntas más.",
    tr: "Günde tek soru: başınız ağrıdı mı? Evetse birkaç kısa soru daha.",
    hi: "दिन में एक सवाल: क्या सिर में दर्द हुआ? हाँ, तो कुछ छोटे सवाल और।",
    sr: "Једно питање дневно: да ли вас је болела глава? Ако јесте, неколико кратких потпитања.",
  },
  calendarMarker: { questionId: "hadPain", intensityQuestionId: "intensity", highlightQuestionId: "tookPainkillers", tone: "warning" },
  settingsSchema: { medications: true },
  episodes: { gateQuestionId: "hadPain", gateValue: true },
  questions: [
    {
      id: "hadPain", type: "yesno",
      text: {
        en: "Did your head hurt today?", ru: "У вас болела голова?", de: "Hatten Sie heute Kopfschmerzen?", fr: "Avez-vous eu mal à la tête aujourd'hui ?",
        es: "¿Te ha dolido la cabeza hoy?", tr: "Bugün başınız ağrıdı mı?", hi: "क्या आज आपके सिर में दर्द हुआ?", sr: "Да ли вас је данас болела глава?",
      },
      labels: {
        yes: { en: "Yes 🤕", ru: "Да 🤕", de: "Ja 🤕", fr: "Oui 🤕", es: "Sí 🤕", tr: "Evet 🤕", hi: "हाँ 🤕", sr: "Да 🤕" },
        no: { en: "No 🙂", ru: "Нет 🙂", de: "Nein 🙂", fr: "Non 🙂", es: "No 🙂", tr: "Hayır 🙂", hi: "नहीं 🙂", sr: "Не 🙂" },
      },
    },
    {
      id: "tookPainkillers", type: "yesno", showIf: painYes,
      text: {
        en: "Did you take painkillers? 💊", ru: "Вы принимали обезболивающие? 💊", de: "Haben Sie Schmerzmittel genommen? 💊", fr: "Avez-vous pris un antidouleur ? 💊",
        es: "¿Tomaste analgésicos? 💊", tr: "Ağrı kesici aldınız mı? 💊", hi: "क्या आपने दर्द की दवा ली? 💊", sr: "Да ли сте узели лек против болова? 💊",
      },
    },
    {
      id: "medication", type: "medication", showIf: { questionId: "tookPainkillers", equals: true },
      text: {
        en: "Which medicine, and what dose?", ru: "Какой препарат? В какой дозировке?", de: "Welches Medikament, in welcher Dosis?", fr: "Quel médicament, et à quelle dose ?",
        es: "¿Qué medicamento y en qué dosis?", tr: "Hangi ilaç, hangi dozda?", hi: "कौन सी दवा और कितनी खुराक?", sr: "Који лек и у којој дози?",
      },
    },
    {
      id: "detailsWanted", type: "yesno", showIf: painYes,
      text: {
        en: "Want to describe how your head hurt?", ru: "Хотите рассказать, как именно у вас болела голова?", de: "Möchten Sie beschreiben, wie es wehgetan hat?",
        fr: "Voulez-vous décrire comment vous avez eu mal ?", es: "¿Quieres describir cómo fue el dolor?", tr: "Nasıl ağrıdığını anlatmak ister misiniz?",
        hi: "क्या बताना चाहेंगे कि सिर कैसे दुखा?", sr: "Желите ли да опишете како вас је болела глава?",
      },
    },
    {
      id: "intensity", type: "scale", min: 1, max: 10, showIf: details,
      text: {
        en: "How intense was the pain?", ru: "Насколько интенсивной была боль?", de: "Wie stark war der Schmerz?", fr: "Quelle était l'intensité de la douleur ?",
        es: "¿Qué intensidad tuvo el dolor?", tr: "Ağrı ne kadar şiddetliydi?", hi: "दर्द कितना तेज़ था?", sr: "Колико је бол био јак?",
      },
      hint: {
        en: "Rate it from 1 to 10, where 10 is the worst pain of your life.",
        ru: "Оцените по 10-балльной шкале, где 10 — самая сильная боль в вашей жизни.",
        de: "Bewerten Sie von 1 bis 10, wobei 10 der stärkste Schmerz Ihres Lebens ist.",
        fr: "Notez de 1 à 10, 10 étant la pire douleur de votre vie.",
        es: "Puntúa de 1 a 10, donde 10 es el peor dolor de tu vida.",
        tr: "1'den 10'a kadar puanlayın; 10, hayatınızdaki en şiddetli ağrıdır.",
        hi: "1 से 10 तक आँकें, जहाँ 10 आपके जीवन का सबसे तेज़ दर्द है।",
        sr: "Оцените од 1 до 10, где је 10 најјачи бол у вашем животу.",
      },
    },
    {
      id: "side", type: "single", showIf: details,
      text: {
        en: "Did the whole head hurt evenly, or one side more?", ru: "Болела вся голова одинаково? Или с одной стороны больше?",
        de: "Tat der ganze Kopf gleichmäßig weh oder eine Seite stärker?", fr: "Toute la tête faisait mal également, ou un côté davantage ?",
        es: "¿Dolía toda la cabeza por igual o más un lado?", tr: "Tüm baş eşit mi ağrıdı, yoksa bir taraf daha mı çok?",
        hi: "क्या पूरा सिर एक जैसा दुखा या एक तरफ़ ज़्यादा?", sr: "Да ли је цела глава болела једнако или једна страна више?",
      },
      options: [
        option("whole", { en: "Whole head evenly", ru: "Вся одинаково", de: "Ganzer Kopf gleichmäßig", fr: "Toute la tête également", es: "Toda por igual", tr: "Tümü eşit", hi: "पूरा सिर एक जैसा", sr: "Цела глава једнако" }),
        option("oneSide", { en: "One side more", ru: "С одной стороны больше", de: "Eine Seite stärker", fr: "Un côté davantage", es: "Más un lado", tr: "Bir taraf daha çok", hi: "एक तरफ़ ज़्यादा", sr: "Једна страна више" }),
        option("other", { en: "Something else", ru: "По-другому", de: "Anders", fr: "Autrement", es: "De otra forma", tr: "Farklı", hi: "कुछ और", sr: "Другачије" }),
      ],
    },
    {
      id: "sideWhich", type: "single", showIf: { questionId: "side", equals: "oneSide" },
      text: { en: "Which side?", ru: "С какой стороны?", de: "Welche Seite?", fr: "Quel côté ?", es: "¿Qué lado?", tr: "Hangi taraf?", hi: "किस तरफ़?", sr: "Која страна?" },
      options: [
        option("left", { en: "Left", ru: "Слева", de: "Links", fr: "Gauche", es: "Izquierdo", tr: "Sol", hi: "बाएँ", sr: "Лева" }),
        option("right", { en: "Right", ru: "Справа", de: "Rechts", fr: "Droite", es: "Derecho", tr: "Sağ", hi: "दाएँ", sr: "Десна" }),
      ],
    },
    {
      id: "pulsating", type: "yesno", showIf: details,
      text: {
        en: "Was the pain pulsating, throbbing?", ru: "Боль пульсировала, стучала?", de: "War der Schmerz pulsierend, pochend?", fr: "La douleur était-elle pulsatile, battante ?",
        es: "¿Era un dolor pulsátil, palpitante?", tr: "Ağrı zonklayıcı, atar gibi miydi?", hi: "क्या दर्द धड़कता, ठोकता हुआ था?", sr: "Да ли је бол пулсирао, лупао?",
      },
    },
    {
      id: "worseOnMovement", type: "yesno", showIf: details,
      text: {
        en: "Did it get worse when you walked or bent over?", ru: "Усиливалась, когда вы ходили или наклонялись?", de: "Wurde es schlimmer beim Gehen oder Bücken?",
        fr: "Empirait-elle en marchant ou en vous penchant ?", es: "¿Empeoraba al caminar o agacharte?", tr: "Yürürken veya eğilirken kötüleşti mi?",
        hi: "क्या चलने या झुकने पर बढ़ता था?", sr: "Да ли се појачавао при ходању или сагињању?",
      },
      labels: {
        yes: { en: "Got worse", ru: "Усиливалась", de: "Wurde schlimmer", fr: "Empirait", es: "Empeoraba", tr: "Kötüleşti", hi: "बढ़ता था", sr: "Појачавао се" },
        no: { en: "Stayed the same", ru: "Не изменялась", de: "Blieb gleich", fr: "Restait pareille", es: "Igual", tr: "Aynı kaldı", hi: "वैसा ही रहा", sr: "Остајао исти" },
      },
    },
    {
      id: "nausea", type: "yesno", showIf: details,
      text: {
        en: "🤢 Did you feel nauseous?", ru: "🤢 Вас тошнило?", de: "🤢 War Ihnen übel?", fr: "🤢 Aviez-vous des nausées ?",
        es: "🤢 ¿Tuviste náuseas?", tr: "🤢 Mideniz bulandı mı?", hi: "🤢 क्या जी मिचलाया?", sr: "🤢 Да ли вам је било мука?",
      },
    },
    {
      id: "light", type: "yesno", showIf: details,
      text: {
        en: "💡 Did light bother you?", ru: "💡 Раздражал свет?", de: "💡 Hat Licht gestört?", fr: "💡 La lumière vous gênait-elle ?",
        es: "💡 ¿Te molestaba la luz?", tr: "💡 Işık rahatsız etti mi?", hi: "💡 क्या रोशनी से परेशानी हुई?", sr: "💡 Да ли вам је сметало светло?",
      },
    },
    {
      id: "sound", type: "yesno", showIf: details,
      text: { en: "🔊 And sounds?", ru: "🔊 А звуки?", de: "🔊 Und Geräusche?", fr: "🔊 Et les sons ?", es: "🔊 ¿Y los sonidos?", tr: "🔊 Ya sesler?", hi: "🔊 और आवाज़ें?", sr: "🔊 А звукови?" },
    },
    {
      id: "triggers", type: "multi", allowOther: true, showIf: details,
      text: {
        en: "What could have triggered the attack?", ru: "Что могло стать провокатором приступа?", de: "Was könnte die Attacke ausgelöst haben?",
        fr: "Qu'est-ce qui a pu déclencher la crise ?", es: "¿Qué pudo desencadenar la crisis?", tr: "Atağı ne tetiklemiş olabilir?",
        hi: "दौरे की वजह क्या हो सकती है?", sr: "Шта је могло да изазове напад?",
      },
      options: [
        option("anxiety", { en: "Anxiety", ru: "Тревога", de: "Angst", fr: "Anxiété", es: "Ansiedad", tr: "Kaygı", hi: "चिंता", sr: "Анксиозност" }),
        option("weather", { en: "Weather", ru: "Погода", de: "Wetter", fr: "Météo", es: "Clima", tr: "Hava durumu", hi: "मौसम", sr: "Време" }),
        option("stress", { en: "Stress", ru: "Стресс", de: "Stress", fr: "Stress", es: "Estrés", tr: "Stres", hi: "तनाव", sr: "Стрес" }),
        option("hunger", { en: "Hunger", ru: "Голод", de: "Hunger", fr: "Faim", es: "Hambre", tr: "Açlık", hi: "भूख", sr: "Глад" }),
        option("exercise", { en: "Physical exertion", ru: "Физ. нагрузки", de: "Körperliche Anstrengung", fr: "Effort physique", es: "Esfuerzo físico", tr: "Fiziksel yorgunluk", hi: "शारीरिक मेहनत", sr: "Физички напор" }),
        option("alcohol", { en: "Alcohol", ru: "Алкоголь", de: "Alkohol", fr: "Alcool", es: "Alcohol", tr: "Alkol", hi: "शराब", sr: "Алкохол" }),
        option("littleSleep", { en: "Too little sleep", ru: "Мало сна", de: "Zu wenig Schlaf", fr: "Trop peu de sommeil", es: "Poco sueño", tr: "Az uyku", hi: "कम नींद", sr: "Премало сна" }),
        option("muchSleep", { en: "Too much sleep", ru: "Много сна", de: "Zu viel Schlaf", fr: "Trop de sommeil", es: "Demasiado sueño", tr: "Çok uyku", hi: "ज़्यादा नींद", sr: "Превише сна" }),
        option("unknown", { en: "Didn't notice", ru: "Не заметил(а)", de: "Nichts bemerkt", fr: "Rien remarqué", es: "No lo noté", tr: "Fark etmedim", hi: "ध्यान नहीं दिया", sr: "Нисам приметио/ла" }),
      ],
    },
    {
      id: "startTime", type: "time", periods: true, showIf: details,
      text: {
        en: "When did the pain start?", ru: "Когда начала болеть голова?", de: "Wann begannen die Kopfschmerzen?", fr: "Quand la douleur a-t-elle commencé ?",
        es: "¿Cuándo empezó el dolor?", tr: "Ağrı ne zaman başladı?", hi: "दर्द कब शुरू हुआ?", sr: "Када је бол почео?",
      },
    },
    {
      id: "endTime", type: "time", periods: true, required: false, showIf: details,
      text: {
        en: "When did it end? Skip if it still hurts.", ru: "Боль закончилась? Пропустите, если ещё болит.", de: "Wann hörte es auf? Überspringen, wenn es noch wehtut.",
        fr: "Quand s'est-elle arrêtée ? Passez si ça fait encore mal.", es: "¿Cuándo terminó? Omite si aún duele.", tr: "Ne zaman geçti? Hâlâ ağrıyorsa atlayın.",
        hi: "कब ख़त्म हुआ? अभी भी दर्द है तो छोड़ें।", sr: "Када је престао? Прескочите ако још боли.",
      },
    },
    {
      id: "note", type: "text", required: false,
      text: {
        en: "Want to add a note?", ru: "Хотите сделать заметку к записи?", de: "Möchten Sie eine Notiz hinzufügen?", fr: "Voulez-vous ajouter une note ?",
        es: "¿Quieres añadir una nota?", tr: "Not eklemek ister misiniz?", hi: "क्या कोई नोट जोड़ना चाहेंगे?", sr: "Желите ли да додате белешку?",
      },
    },
  ],
};
